"use client";

import { Button } from "@/components/rac/button";
import { useAuth } from "@/hooks/use-auth";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { User } from "lucide-react";
import { DesktopTrigger } from "./desktop-trigger";
import { SignInDropdown } from "./dropdown";
import { SignInProps } from "./types";
import { getAvatarUrl, getInitials } from "./utils";

export function SignIn({ mobile = false }: SignInProps) {
	const { isAuthenticated, user, isLoading } = useAuth();
	const { openDialog } = useLoginDialog();

	// Loading state - ensure consistent structure between server and client
	if (isLoading) {
		return mobile ? (
			<div className="w-full h-12 rounded-lg bg-gray-200 animate-pulse" />
		) : (
			// Match the exact button structure and styling
			<div className="btn-clickable flex items-center gap-2 rounded-md h-10 px-3 py-2 bg-transparent border border-gray-200 w-[120px]">
				<div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse" />
				<div className="hidden lg:block h-4 bg-gray-200 animate-pulse rounded flex-1" />
			</div>
		);
	}

	// Authenticated state
	if (isAuthenticated && user) {
		// For mobile, return null since mobile menu handles authentication UI
		if (mobile) {
			return null;
		}

		// For desktop, use the dropdown
		const trigger = <DesktopTrigger user={user} getInitials={getInitials} getAvatarUrl={getAvatarUrl} />;
		return <SignInDropdown trigger={trigger} />;
	}

	// Not authenticated state
	if (mobile) {
		return (
			<Button
				variant="default"
				className="w-full h-12 rounded-lg font-medium text-base flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98]"
				aria-label="Log in to your account"
				onPress={() => openDialog()}
			>
				Log in
			</Button>
		);
	}

	return (
		<Button
			variant="outline"
			className="h-10 px-3 py-2 bg-transparent border border-gray-200 hover:border-gray-300 w-[120px] flex items-center gap-2 focus:bg-transparent data-[focus-visible]:bg-transparent group"
			aria-label="Log in to your account"
			onPress={() => openDialog()}
		>
			<div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
				<User className="h-4 w-4 text-gray-500" />
			</div>
			<span className="hidden lg:block text-sm font-medium text-gray-700 group-hover:text-primary-foreground truncate flex-1">
				Log in
			</span>
		</Button>
	);
}
